import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgSplitButton } from "@intelligems/ig-theme/IgSplitButton";
import { IgTooltip } from "@intelligems/ig-theme/IgTooltip";
import { Stack } from "@mui/material";
import { CloudUpload, Play, PowerOff, Save } from "lucide-react";

/**
 * The workspace's write actions, as a flat prop bag.
 *
 * Flat rather than the view model for the reason {@link TitleBarProps}
 * documents: `strictFunctionTypes` makes a `WorkspaceViewModel<GateKey, TabKey>`
 * non-assignable to `<string, string>`, so a shared component that took the
 * view model would have to be generic in both keys for no benefit.
 */
interface WorkspaceActionsSharedProps {
  /**
   * Hover copy explaining a disabled state that is not self-evident. Wraps the
   * whole cluster, because the same blocker (a run in flight, pending edits)
   * usually disables more than one button at once.
   */
  disabledActionHint?: string;
  isSaveDisabled: boolean;
  /** `null` when the domain's port supplies no gate-run capability. */
  runTests: { isDisabled: boolean } | null;
  /** `null` when the domain's port supplies no unpublish capability. */
  unpublish: { isDisabled: boolean; disabledHint?: string } | null;
  onRunTests: () => void;
  onUnpublish: () => void;
}

/**
 * The title bar's flat row, for a domain that really does have a draft: save
 * and publish are separate verbs because saving does not serve anything.
 */
export interface WorkspaceActionsClusterProps extends WorkspaceActionsSharedProps {
  presentation?: "cluster";
  hasUnsavedChanges: boolean;
  isPublishDisabled: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
}

/**
 * One Save, with its family behind a caret.
 *
 * Used by domains with no draft state: the version a save writes is the version
 * that serves, so there is no second "publish" verb to offer — Save runs the
 * gates and deploys, and a failing gate is what stops it.
 */
export interface WorkspaceActionsSplitProps extends WorkspaceActionsSharedProps {
  presentation: "split";
  /** Persist the current buffer. Whether that also deploys is the host's call. */
  onSave: () => void;
  /**
   * What the Save control should SAY about the buffer beside it.
   *
   * - `"pending_edits"` (the default, and every host's behaviour before this
   *   prop existed) — "Save": there is something on screen to persist.
   * - `"settled"` — "Saved": the buffer matches what is persisted.
   *
   * Only the LABEL moves, and deliberately — see the `color` comment in
   * {@link SplitActions} for why the settled state cannot currently also be
   * carried by the button's colour. Save stays ENABLED in both states,
   * for the reason `saveAvailability.ts` documents at length: re-saving an
   * unchanged rule is a well-defined idempotent act, and the states where the
   * merchant most needs it (a failed publish, a settings edit that autosaved
   * onto the row) are exactly the ones with a clean buffer. A button that goes
   * grey there is the defect, not the fix — what was wrong was that it stayed
   * blue and said "Save" with nothing to save, so the merchant could not tell
   * saved from unsaved and assumed pending work.
   *
   * Optional with a back-compatible default because the cluster's sibling
   * hosts (shipping providers and the other codegen workspaces) share this
   * component and have not been asked to compute it.
   */
  saveState?: "pending_edits" | "settled";
  /** Domain-specific label, without changing the shared action's behavior. */
  saveLabel?: string;
  /** Explicit impact choice when the current rule serves multiple experiences. */
  saveImpact: {
    experienceCount: number;
    onSaveToAll: () => void;
  } | null;
  /**
   * Fork the current source into a rule of its own. `null` on a creation
   * screen — there is no existing rule to fork from yet, and a screen with
   * nothing behind the caret renders a plain button.
   */
  saveAsNewRule: { isDisabled: boolean; onSelect: () => void } | null;
}

export type WorkspaceActionsProps =
  | WorkspaceActionsClusterProps
  | WorkspaceActionsSplitProps;

/**
 * The workspace's write actions, in whichever shape the host page wants.
 *
 * Extracted from the title bar so an embedded workspace — one whose title bar
 * is suppressed because the page around it owns the header — can host the same
 * actions without reimplementing which one is disabled when. There is one
 * definition of these actions; there are two places to put it.
 */
export const WorkspaceActions = (props: WorkspaceActionsProps) =>
  props.presentation === "split" ? (
    <SplitActions {...props} />
  ) : (
    <ClusterActions {...props} />
  );

const ClusterActions = ({
  hasUnsavedChanges,
  disabledActionHint,
  isSaveDisabled,
  runTests,
  isPublishDisabled,
  unpublish,
  onSaveDraft,
  onRunTests,
  onPublish,
  onUnpublish,
}: WorkspaceActionsClusterProps) => (
  <IgTooltip title={disabledActionHint ?? ""}>
    {/* span keeps the tooltip reachable while the buttons are disabled */}
    <Stack component="span" direction="row" alignItems="center" spacing={1}>
      <IgButton
        variant="outlined"
        size="mini"
        color={hasUnsavedChanges ? "warning" : "secondary"}
        startIcon={<Save size={14} />}
        onClick={onSaveDraft}
        disabled={isSaveDisabled}
      >
        {hasUnsavedChanges ? "Save changes" : "Save draft"}
      </IgButton>
      {runTests ? (
        <IgButton
          variant="outlined"
          size="mini"
          color="secondary"
          startIcon={<Play size={14} />}
          onClick={onRunTests}
          disabled={runTests.isDisabled}
        >
          Run tests
        </IgButton>
      ) : null}
      <IgButton
        variant="contained"
        size="mini"
        color="primary"
        startIcon={
          <CloudUpload
            size={14}
            color={isPublishDisabled ? undefined : "white"}
          />
        }
        onClick={onPublish}
        disabled={isPublishDisabled}
      >
        Publish
      </IgButton>
      {unpublish ? (
        <IgTooltip title={unpublish.disabledHint ?? ""}>
          <span>
            <IgButton
              variant="outlined"
              size="mini"
              color="warning"
              startIcon={<PowerOff size={14} />}
              onClick={onUnpublish}
              disabled={unpublish.isDisabled}
            >
              Unpublish
            </IgButton>
          </span>
        </IgTooltip>
      ) : null}
    </Stack>
  </IgTooltip>
);

const SplitActions = ({
  disabledActionHint,
  isSaveDisabled,
  runTests,
  unpublish,
  onSave,
  saveState = "pending_edits",
  saveLabel,
  saveImpact,
  saveAsNewRule,
  onRunTests,
  onUnpublish,
}: WorkspaceActionsSplitProps) => (
  <IgSplitButton
    // On the main button only: the caret's options carry their own reasons
    // as menu-item tooltips, and the split button withholds this one while
    // its menu is open.
    tooltip={disabledActionHint}
    // One colour in both states, and NOT because a settled Save deserves the
    // page's loudest blue — because `IgSplitButton` cannot currently draw a
    // quiet one. It shares `color` with its caret and paints the caret a
    // hard-coded white chevron (`<IgIconComponent color="white" />`), so any
    // light-ground colour renders white-on-near-white: `secondary` is
    // `#F7F7F7`, and the caret becomes a blank square. That caret is the
    // ONLY route to "Save to all N experiences", "Save as new rule" and
    // "Run tests", and a clean buffer is the default on every page load, so
    // the quieter look would have hidden the whole menu in the common state.
    // The buffer's state is carried by the label instead ("Save" / "Saved").
    //
    // The real fix is in ig-theme — colour that chevron from the button's
    // own contrast colour rather than a literal — and it is out of this lane.
    color="primary"
    // The primary alone. The caret's options have their own reasons to be
    // unavailable, and a rule with nothing left to save is exactly when the
    // author reaches for one of them.
    disabled={isSaveDisabled}
    // The 40px control: level with the 40px Cancel beside it.
    height={40}
    menuOptions={[
      {
        // "Save", not "Save changes": one verb, and the state of the buffer
        // is carried by which word it is rather than by a longer label. The
        // cluster keeps its draft wording because it really does have both a
        // save and a publish verb.
        key: saveLabel ?? (saveState === "settled" ? "Saved" : "Save"),
        hidden: true,
        action: onSave,
      },
      ...(saveImpact
        ? [
            {
              key:
                saveLabel ??
                `Save to all ${saveImpact.experienceCount} experiences`,
              disabledMessage: isSaveDisabled
                ? (disabledActionHint ?? "Saving is unavailable.")
                : undefined,
              action: saveImpact.onSaveToAll,
            },
          ]
        : []),
      ...(saveAsNewRule
        ? [
            {
              key: saveImpact ? "Save as new rule…" : "Save as new rule",
              disabledMessage: saveAsNewRule.isDisabled
                ? (disabledActionHint ?? "Save as new rule is unavailable.")
                : undefined,
              action: saveAsNewRule.onSelect,
            },
          ]
        : []),
      ...(runTests
        ? [
            {
              key: "Run tests",
              disabledMessage: runTests.isDisabled
                ? (disabledActionHint ?? "Run tests is unavailable.")
                : undefined,
              action: onRunTests,
            },
          ]
        : []),
      ...(unpublish
        ? [
            {
              key: "Unpublish",
              disabledMessage: unpublish.isDisabled
                ? (unpublish.disabledHint ??
                  disabledActionHint ??
                  "Unpublish is unavailable.")
                : undefined,
              action: onUnpublish,
            },
          ]
        : []),
    ]}
  />
);
